import { useNavigate, type NavigateFunction, useLocation } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import routes from "./config";
import { PATHS, type RouteKey, routeKeyFromPath } from "./paths";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

// Pro Route ein Titel je Sprache. Lookup per RouteKey, nicht per Pfad,
// damit DE- und EN-Slugs derselben Seite denselben Titel-Eintrag teilen.
const TITLES: Record<RouteKey, { de: string; en: string }> = {
  home: {
    de: 'Hotel Alpenhof Gauting | Hotel am Starnberger See & nahe München',
    en: 'Hotel Alpenhof Gauting | Hotel near Lake Starnberg & Munich',
  },
  rooms: {
    de: 'Zimmer & Preise | Hotel Alpenhof Gauting – Hotel nahe München & Starnberger See',
    en: 'Rooms & Rates | Hotel Alpenhof Gauting – Hotel near Munich & Lake Starnberg',
  },
  hotel: {
    de: 'Das Hotel | Hotel Alpenhof Gauting – Tradition & Gastfreundschaft',
    en: 'The Hotel | Hotel Alpenhof Gauting – Tradition & Hospitality',
  },
  ausflugsziele: {
    de: 'Ausflugsziele | Hotel Alpenhof Gauting – Starnberger See, München & Alpen',
    en: 'Things to do | Hotel Alpenhof Gauting – Lake Starnberg, Munich & the Alps',
  },
  buchen: {
    de: 'Zimmer buchen | Hotel Alpenhof Gauting – Hotel am Starnberger See',
    en: 'Book a room | Hotel Alpenhof Gauting – Hotel near Lake Starnberg',
  },
  impressum:   { de: 'Impressum | Hotel Alpenhof Gauting',   en: 'Imprint | Hotel Alpenhof Gauting' },
  datenschutz: { de: 'Datenschutz | Hotel Alpenhof Gauting', en: 'Privacy | Hotel Alpenhof Gauting' },
  agb:         { de: 'AGB | Hotel Alpenhof Gauting',         en: 'Terms & Conditions | Hotel Alpenhof Gauting' },
};

// Eigene Meta-Description je Seite und Sprache. Lookup per RouteKey, damit
// DE- und EN-Slugs derselben Seite denselben Eintrag teilen.
const META: Record<RouteKey, { de: string; en: string }> = {
  home: {
    de: 'Hotel Alpenhof Gauting – Ihr charmantes Hotel am Starnberger See, nur 25 Min. von München. Stilvolle Zimmer, Frühstück mit Bio-Produkten, kostenlose Parkplätze.',
    en: 'Hotel Alpenhof Gauting – your charming hotel near Lake Starnberg, just 25 min from Munich. Stylish rooms, organic breakfast and free parking.',
  },
  rooms: {
    de: 'Gemütliche Zimmer im Hotel Alpenhof Gauting – mit Bio-Frühstück, kostenlosen Parkplätzen und kurzer Anfahrt nach München. Preise ansehen und direkt buchen.',
    en: 'Comfortable rooms at Hotel Alpenhof Gauting – with organic breakfast, free parking and a short drive to Munich. View our rates and book directly.',
  },
  hotel: {
    de: 'Familiär geführtes Hotel in Gauting am Starnberger See. Lernen Sie den Alpenhof kennen: Tradition, Gastfreundschaft und Ambiente nahe München.',
    en: 'Family-run hotel in Gauting near Lake Starnberg. Discover the Alpenhof: tradition, hospitality and a warm atmosphere close to Munich.',
  },
  ausflugsziele: {
    de: 'Ausflugsziele rund um den Alpenhof Gauting: Starnberger See, München und die Alpen. Tipps für Ausflüge, Natur und Kultur in der Region.',
    en: 'Things to do around the Alpenhof Gauting: Lake Starnberg, Munich and the Alps. Tips for excursions, nature and culture in the region.',
  },
  buchen: {
    de: 'Zimmer im Hotel Alpenhof Gauting direkt buchen – Hotel am Starnberger See, nur 25 Min. von München. Bio-Frühstück und kostenlose Parkplätze inklusive.',
    en: 'Book your room at Hotel Alpenhof Gauting directly – hotel near Lake Starnberg, just 25 min from Munich. Organic breakfast and free parking included.',
  },
  impressum: {
    de: 'Impressum des Hotel Alpenhof Gauting – Anbieterkennzeichnung und rechtliche Angaben.',
    en: 'Imprint of Hotel Alpenhof Gauting – provider identification and legal information.',
  },
  datenschutz: {
    de: 'Datenschutzerklärung des Hotel Alpenhof Gauting – Informationen zum Umgang mit Ihren personenbezogenen Daten.',
    en: 'Privacy policy of Hotel Alpenhof Gauting – information on how we handle your personal data.',
  },
  agb: {
    de: 'Allgemeine Geschäftsbedingungen des Hotel Alpenhof Gauting.',
    en: 'Terms and conditions of Hotel Alpenhof Gauting.',
  },
};

// Fallback-Description, falls eine URL keinem RouteKey zugeordnet werden kann.
const META_FALLBACK_DE = META.home.de;
const META_FALLBACK_EN = META.home.en;

const SEO_H1_DE = 'Hotel Alpenhof Gauting – Hotel am Starnberger See nahe München';
const SEO_H1_EN = 'Hotel Alpenhof Gauting – hotel near Lake Starnberg, close to Munich';

// Produktiv-Domain für canonical / hreflang. Vor Live-Gang ggf. anpassen.
const PROD_ORIGIN = 'https://www.alpenhof-gauting.de';

// Breadcrumb-Labels pro Sprache.
const BREADCRUMBS: Record<RouteKey, { de: string; en: string }> = {
  home:          { de: 'Startseite',    en: 'Home' },
  rooms:         { de: 'Zimmer & Preise', en: 'Rooms & Rates' },
  hotel:         { de: 'Das Hotel',     en: 'The Hotel' },
  ausflugsziele: { de: 'Ausflugsziele', en: 'Things to do' },
  buchen:        { de: 'Buchen',        en: 'Book' },
  impressum:     { de: 'Impressum',     en: 'Imprint' },
  datenschutz:   { de: 'Datenschutz',   en: 'Privacy' },
  agb:           { de: 'AGB',           en: 'Terms' },
};

const BREADCRUMB_SCRIPT_ID = 'breadcrumb-jsonld';

function setBreadcrumb(key: RouteKey, lang: 'de' | 'en') {
  // Home selbst braucht keinen Crumb (wäre nur ein Element)
  const items = key === 'home'
    ? [{ '@type': 'ListItem', position: 1, name: BREADCRUMBS.home[lang], item: `${PROD_ORIGIN}${PATHS.home[lang]}` }]
    : [
        { '@type': 'ListItem', position: 1, name: BREADCRUMBS.home[lang], item: `${PROD_ORIGIN}${PATHS.home[lang]}` },
        { '@type': 'ListItem', position: 2, name: BREADCRUMBS[key][lang], item: `${PROD_ORIGIN}${PATHS[key][lang]}` },
      ];
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
  let script = document.getElementById(BREADCRUMB_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = BREADCRUMB_SCRIPT_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function setMeta(name: string, content: string) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.setAttribute('content', content);
}

function setOg(prop: string, content: string) {
  const el = document.querySelector(`meta[property="${prop}"]`);
  if (el) el.setAttribute('content', content);
}

// robots-Meta idempotent setzen/entfernen. Wird genutzt, um die EN-Rechts-URLs
// (/imprint, /privacy, /terms) auf noindex zu setzen: Sie tragen denselben
// deutschen Text wie die DE-Seiten und sollen nicht als Duplikat indexiert werden.
function setRobots(noindex: boolean) {
  let el = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
  if (noindex) {
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, follow');
  } else if (el) {
    el.remove();
  }
}

// Rechtsseiten haben zwar eigene EN-Slugs, aber nur deutschen Text.
const LEGAL_KEYS: ReadonlySet<RouteKey> = new Set(['impressum', 'datenschutz', 'agb']);

// hreflang/canonical-Link-Tags im <head> idempotent setzen.
function setLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selector = Object.entries(attrs).reduce(
    (sel, [k, v]) => `${sel}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  );
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearAlternates() {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
}

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Wenn die URL einen Slug enthält, der nur in einer Sprache existiert,
  // i18n entsprechend ausrichten (z. B. /rooms → en, /zimmer → de).
  useEffect(() => {
    const key = routeKeyFromPath(location.pathname);
    if (!key) return;
    const isEnglishSlug = PATHS[key].en === location.pathname && PATHS[key].de !== location.pathname;
    const isGermanSlug  = PATHS[key].de === location.pathname && PATHS[key].en !== location.pathname;
    if (isEnglishSlug && i18n.language !== 'en') i18n.changeLanguage('en');
    else if (isGermanSlug && i18n.language === 'en') i18n.changeLanguage('de');
  }, [location.pathname, i18n]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const lang: 'de' | 'en' = i18n.language === 'en' ? 'en' : 'de';
    const key = routeKeyFromPath(location.pathname);
    const fallbackTitle = lang === 'en'
      ? 'Hotel Alpenhof Gauting | Hotel near Lake Starnberg & Munich'
      : 'Hotel Alpenhof Gauting | Hotel Starnberger See & nahe München';
    document.title = key ? TITLES[key][lang] : fallbackTitle;
    document.documentElement.lang = lang;
    const description = key
      ? META[key][lang]
      : (lang === 'en' ? META_FALLBACK_EN : META_FALLBACK_DE);
    setMeta('description', description);
    setMeta('content-language', lang);
    setOg('og:locale', lang === 'en' ? 'en_GB' : 'de_DE');
    setOg('og:title', document.title);
    setOg('og:description', description);

    const seoH1 = document.getElementById('seo-h1');
    if (seoH1) seoH1.textContent = lang === 'en' ? SEO_H1_EN : SEO_H1_DE;

    // canonical + hreflang. Auf der Test-Domain (HashRouter) zeigen die URLs
    // noch nicht produktiv; sobald die Live-Domain mit BrowserRouter steht,
    // greift dieselbe Logik direkt korrekt.
    if (key) {
      const dePath = PATHS[key].de;
      const enPath = PATHS[key].en;
      const currentPath = lang === 'en' ? enPath : dePath;
      setLink('canonical', `${PROD_ORIGIN}${currentPath}`);

      clearAlternates();
      setLink('alternate', `${PROD_ORIGIN}${dePath}`,        { hreflang: 'de' });
      setLink('alternate', `${PROD_ORIGIN}${enPath}`,        { hreflang: 'en' });
      setLink('alternate', `${PROD_ORIGIN}${dePath}`,        { hreflang: 'x-default' });
      setOg('og:url', `${PROD_ORIGIN}${currentPath}`);

      // EN-Rechts-URLs (deutscher Text) auf noindex; DE-Versionen bleiben indexierbar.
      setRobots(LEGAL_KEYS.has(key) && currentPath === enPath);

      setBreadcrumb(key, lang);
    } else {
      setRobots(false);
    }

    const timer = setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).dirs21) {
        (window as any).dirs21.rebindTriggers();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, i18n.language]);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  return element;
}
